/* eslint-disable react-refresh/only-export-components */
import { RotateCcw, Save } from "lucide-react";
import { EditBase, Form, useNotify, useTranslate } from "ra-core";
import type { ReactNode } from "react";
import { useFormContext } from "react-hook-form";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";

import { useInboundEmail } from "../misc/useInboundEmail";
import { useConfigurationUpdater } from "../root/ConfigurationContext";
import { defaultConfiguration } from "../root/defaultConfiguration";
import { transformFormValues, useSettingsDefaultValues } from "./settingsForm";
import {
  SETTINGS_SECTIONS,
  type SettingsSectionId,
} from "./settingsSectionRegistry";

// SettingsPage.test.ts reads it from here, where it lived before the sections
// moved out.
export { validateItemsInUse } from "./settingsForm";

// Grouped by what each card configures. Every id is an element on the page
// that the nav scrolls to: groups are <section>s, sections are <Card>s. The
// sections themselves, and their labels, are in settingsSectionRegistry.ts.
const SECTION_GROUPS: {
  id: string;
  label: string;
  fallback: string;
  sections: SettingsSectionId[];
}[] = [
  {
    id: "general",
    label: "crm.settings.sections.general",
    fallback: "General",
    sections: ["branding", "whitelist", "inbound_email"],
  },
  {
    id: "contacts",
    label: "resources.contacts.name",
    fallback: "Contacts",
    sections: ["record_types", "note_statuses", "note_channels", "tags"],
  },
  {
    id: "deals",
    label: "resources.deals.name",
    fallback: "Deals",
    sections: [
      "currency",
      "deal_stages",
      "pipeline_statuses",
      "deal_categories",
    ],
  },
  {
    id: "tasks",
    label: "resources.tasks.name",
    fallback: "Tasks",
    sections: ["task_types"],
  },
];

const NavItem = ({
  id,
  className,
  children,
}: {
  id: string;
  className?: string;
  children: ReactNode;
}) => (
  <button
    type="button"
    onClick={() => {
      document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
    }}
    className={cn(
      "block w-full text-left px-3 py-1 text-sm rounded-md hover:text-foreground hover:bg-muted transition-colors",
      className,
    )}
  >
    {children}
  </button>
);

const GroupHeading = ({ children }: { children: ReactNode }) => (
  <h2 className="text-sm font-medium uppercase tracking-wide text-muted-foreground px-1">
    {children}
  </h2>
);

export const SettingsPage = () => {
  const updateConfiguration = useConfigurationUpdater();
  const notify = useNotify();

  return (
    <EditBase
      resource="configuration"
      id={1}
      mutationMode="pessimistic"
      redirect={false}
      transform={transformFormValues}
      mutationOptions={{
        onSuccess: (data: any) => {
          updateConfiguration(data.config);
          notify("crm.settings.saved");
        },
        onError: () => {
          notify("crm.settings.save_error", {
            type: "error",
          });
        },
      }}
    >
      <SettingsForm />
    </EditBase>
  );
};

SettingsPage.path = "/settings";

const SettingsForm = () => {
  const defaultValues = useSettingsDefaultValues();

  return (
    <Form defaultValues={defaultValues}>
      <SettingsFormFields />
    </Form>
  );
};

const SettingsFormFields = () => {
  const translate = useTranslate();
  const {
    reset,
    formState: { isSubmitting },
  } = useFormContext();

  // Empty when the inbound_email function is undeployed, its secret is unset,
  // or the app runs on FakeRest. The card and its nav entry both drop out.
  const inboundEmail = useInboundEmail();
  const groups = SECTION_GROUPS.map((group) => ({
    ...group,
    sections: group.sections.filter(
      (section) => section !== "inbound_email" || inboundEmail,
    ),
  }));

  return (
    <div className="max-w-4xl mx-auto flex gap-8 mt-4 pb-20">
      {/* Left navigation */}
      <nav className="hidden md:block w-48 shrink-0">
        <div className="sticky top-4 space-y-1">
          <h1 className="text-2xl font-semibold px-3 mb-2">
            {translate("crm.settings.title")}
          </h1>
          {groups.map((group) => (
            <div key={group.id} className="pt-2">
              <NavItem
                id={group.id}
                className="text-xs font-medium uppercase tracking-wide text-muted-foreground"
              >
                {translate(group.label, { smart_count: 2, _: group.fallback })}
              </NavItem>
              {group.sections.map((section) => (
                <NavItem key={section} id={section} className="pl-6">
                  {translate(SETTINGS_SECTIONS[section].label, {
                    smart_count: 2,
                    _: SETTINGS_SECTIONS[section].fallback,
                  })}
                </NavItem>
              ))}
            </div>
          ))}
        </div>
      </nav>

      {/* Main content, grouped by what each card configures. One card per
          section; the same section components open in a dialog from the
          settings icons around the app (SettingsShortcut). */}
      <div className="flex-1 min-w-0 max-w-2xl space-y-10">
        {groups.map((group) => (
          <section key={group.id} id={group.id} className="space-y-4">
            <GroupHeading>
              {translate(group.label, { smart_count: 2, _: group.fallback })}
            </GroupHeading>
            {group.sections.map((section) => {
              const { Component } = SETTINGS_SECTIONS[section];
              return (
                <Card key={section} id={section}>
                  <CardContent>
                    <Component />
                  </CardContent>
                </Card>
              );
            })}
          </section>
        ))}
      </div>

      {/* Sticky save button */}
      <div className="fixed bottom-0 left-0 right-0 border-t bg-background p-4">
        <div className="max-w-4xl mx-auto flex gap-8 px-4">
          <div className="hidden md:block w-48 shrink-0" />
          <div className="flex-1 min-w-0 max-w-2xl flex justify-between">
            <Button
              type="button"
              variant="ghost"
              onClick={() =>
                reset({
                  ...defaultConfiguration,
                  lightModeLogo: {
                    src: defaultConfiguration.lightModeLogo,
                  },
                  darkModeLogo: { src: defaultConfiguration.darkModeLogo },
                })
              }
            >
              <RotateCcw className="h-4 w-4 mr-1" />
              {translate("crm.settings.reset_defaults")}
            </Button>
            <div className="flex gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => window.history.back()}
              >
                {translate("ra.action.cancel")}
              </Button>
              <Button type="submit" disabled={isSubmitting}>
                <Save className="h-4 w-4 mr-1" />
                {isSubmitting
                  ? translate("crm.settings.saving")
                  : translate("ra.action.save")}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
